import React, { PureComponent } from 'react';

import { select as d3Select, mouse as d3Mouse } from 'd3-selection';
import { scaleLinear as d3ScaleLinear } from 'd3-scale';
import { scaleTime as d3ScaleTime } from 'd3-scale';
import { stack as d3Stack,
         stackOrderNone as d3StackOrderNone,
         stackOffsetNone as d3StackOffsetNone} from 'd3-shape';
import { nest as d3Nest } from 'd3-collection';
import { extent as d3Extent, max as d3Max } from 'd3-array';
import { transition as d3Transition } from 'd3-transition';

import debounce from 'lodash.debounce';
import { isDesktop, elementInHorizontalViewport, getYPos } from '../../helper/utils';

import { createLayer,
         createVerticalLine,
         createXAxis,
         createMarkerLabel } from './helper';

import { isMobile } from '../../helper/utils';

import './areachart.scss';

export default class AreaChart extends PureComponent {
  constructor() {
    super();

    this.onResize = debounce(this.onResize.bind(this), 200);
    window.addEventListener('resize', this.onResize);
  }

  componentDidMount() {
    this.createChart(this.props.data);

    document.querySelector('.section.intro').style.minHeight = 0;
  }

  createChart(data) {
    if (!data) return null;

    this.width = isMobile ? window.innerWidth : document.body.clientWidth;

    const margin = { top: 10, right: 0, bottom: 0, left: 0 };
    const root = this.refs.chartContainer;
    const width = this.width - margin.left - margin.right;
    const MAX_HEIGHT = 300 - margin.top - margin.bottom;
    const height = Math.max(250, width * .4 > MAX_HEIGHT ? MAX_HEIGHT : width * .4);
    const outerWidth = width + margin.left + margin.right;
    const outerHeight = height + margin.top + margin.bottom;

    const svg = d3Select(root)
      .html('')
      .append('svg')
      .classed('bkr--area-chart', true)
      .attr('width', outerWidth)
      .attr('height', outerHeight)
      .append('g')
      .attr('transform', `translate(${margin.right}, ${margin.top})`);

    const x = d3ScaleTime()
      .range([0, width]);

    const y = d3ScaleLinear()
      .range([height, 0]);

    data = data.map(d => {
      d.areaValue = +d.areaValue;
      d.rawProdValue = d.rawProdValue;
      d.rank = d.rank;
      return d;
    }).filter(d => {
      return true;
    });

    const countryKeys = data.filter(d => {
      return d.date === 2014;
    })
    .sort((a,b) => b.areaValue - a.areaValue)
    .map( d => d.country )
    .slice(0, 20);

    data = data.filter(d => countryKeys.indexOf(d.country) >= 0);

    const nestYear = d3Nest().key(d => d.date);
    const nestedDataByYear = nestYear.entries(data);
    const stackData = nestedDataByYear.map(d => {
      d.date = +d.key;
      d.values.forEach((v, i) => {
        d[v.country] = v.areaValue;
        d[`${v.country}_rawProd`] = v.rawProdValue;
        d[`${v.country}_rank`] = v.rank;
      });
      delete d.key;
      delete d.values;
      return d;
    });

    const nest = d3Nest().key(d => d.country);

    const nestedData = nest.entries(data);

    const z = d3ScaleLinear()
      .domain([nestedData.length-1, 0])
      .range(['#3E200F', '#814E29']);

    let stack = d3Stack()
      .keys(countryKeys)
      .order(d3StackOrderNone)
      .offset(d3StackOffsetNone);

    const stackedData = stack(stackData)

    const xDomainBoundries = d3Extent(data.map(d => d.date));
    x.domain(d3Extent(data, d => new Date(d.date, 0, 1)));
    y.domain([0, d3Max(stackedData[stackedData.length - 1], d => d[1])]);

    const layer = createLayer(svg, stackedData, x, y, z);

    const xAxisEl = createXAxis(svg, height, x);
    const verticalLine = createVerticalLine();
    const markerLabel = createMarkerLabel(svg, stackedData);

    layer
      .on('mouseover', mousemove)
      .on('mousemove', mousemove)
      .on('mouseout', mouseleaveArea);

    d3Select('.bkr--area-chart')
      .on('mouseleave', mouseleaveSvgContainer)

    d3Select('.area-chart-info')
      .style('display', 'block');

    function mousemove(d) {
      const mousex = d3Mouse(this)[0];
      let currenDataObj = (x.invert(mousex));
      const currentYear = currenDataObj.getMonth() > 5 ? currenDataObj.getFullYear() + 1 : currenDataObj.getFullYear();
      let xPos = null;
      // getCurrentDate via invert from X scale
      // add Offset for first and Last Year
      if (currentYear === xDomainBoundries[0]) {
        xPos = x(new Date(currentYear, 0, 1)) + 10;
      }
      else if (currentYear === 2014) {
        xPos = x(new Date(currentYear, 0, 1)) - 10;
      }
      else {
        xPos = x(new Date(currentYear, 0, 1));
      }
      // highlight area
      d3Select(this)//.transition(transition(50))
        .style('opacity', .925)
        .style('z-index', '1000');

      // select the last country from data,
      // to get the max height for the vertical line
      const stackedCountryObj = stackedData[stackedData.length-1].filter(d => d.data.date === currentYear);
      const currentHeight = height - y(stackedCountryObj[0][1]) - margin.top - 10;

      // toggle vertical line and xAxis Scale
      verticalLine//.transition(transition(50))
        .style('height', currentHeight + 'px')
        .style('opacity', .2)
        .style('left', (xPos) + 'px' );

      xAxisEl.style('display', 'block');

      const currentObj = d.filter(d => d.data.date === currentYear)[0];
      const oldObj = d[0];

      const textStart = elementInHorizontalViewport(xPos, width) ? 'start' : 'end';
      const xPoswithOffset = elementInHorizontalViewport(xPos, width) ? xPos + 10 : xPos - 10;
      const yPos = getYPos(y(currentObj[1]), height);

      svg.selectAll('.marker-label')
        .text(`${d.key}`)
        .attr('y', yPos + 3)
        .attr('x', xPoswithOffset)
        .attr('text-anchor', textStart)
        .style('fill', '#eee')
        .style('fill-opacity', .9);

      svg.selectAll('.marker-label')
        .append('tspan')
        .attr('y', yPos + 20)
        .attr('x', xPoswithOffset)
        .text(`Erntefläche: ${currentObj.data[d.key].toLocaleString('de-DE')} ha`);

      svg.selectAll('.marker-label')
        .append('tspan')
        .attr('y', yPos + 35)
        .attr('x', xPoswithOffset)
        .text('Anteil der jährlichen');

      svg.selectAll('.marker-label')
        .append('tspan')
        .attr('y', yPos + 50)
        .attr('x', xPoswithOffset)
        .text('Rohkaffeeproduktion: ' + currentObj.data[`${d.key}_rawProd`] + '%');

      svg.selectAll('.marker-label')
        .append('tspan')
        .attr('y', yPos + 65)
        .attr('x', xPoswithOffset)
        .text(`Rang: ${currentObj.data[`${d.key}_rank`]}`)
    }

    function mouseleaveArea() {
      // remove area highlighted
      d3Select(this).transition(transition(50))
        .style('opacity', 1)
        .style('z-index', 'inherit');
    }

    function mouseleaveSvgContainer() {
      verticalLine.transition(transition(50))
        .style('opacity', 0);

      if (isDesktop) {
        markerLabel.transition(transition(50))
          .style('fill-opacity', 0);
      }
    }

    function transition(duration) {
      return d3Transition().duration(duration);
    }
  }

  onResize() {
    if (this.width !== window.innerWidth) {
      this.createChart(this.props.data);
    }
  }

  render() {
    return (
      <div className="area-chart--wrapper">
        <div ref="chartContainer" className="chartContainer"></div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/Component/AreaChart/index.js